import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail, describeApiError } from "@/lib/api";
import { isAgricultureBusiness } from "@/lib/industries";
import { Calculator, FilePdf, PaperPlaneTilt, CircleNotch, Barn, ShieldCheck, WarningCircle, CheckCircle, XCircle } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const money = (n) => `$${(Number(n) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function LineTable({ title, rows, totalLabel, totalValue, testid }) {
  return (
    <div className="rounded-[24px] border p-5 bg-white" style={{ borderColor: "var(--bk-border)" }} data-testid={testid}>
      <h5 className="font-display text-lg font-bold mb-3" style={{ color: "var(--bk-text)" }}>{title}</h5>
      <div className="space-y-2">
        {rows.map((row) => (
          <div key={row.line} className="flex items-center justify-between gap-3 py-2 border-b" style={{ borderColor: "var(--bk-border)" }}>
            <span className="text-sm" style={{ color: "var(--bk-text-2)" }}>{row.line}</span>
            <span className="text-sm font-semibold text-right" style={{ color: "var(--bk-text)" }}>{money(row.amount)}</span>
          </div>
        ))}
        <div className="flex items-center justify-between gap-3 pt-2">
          <span className="text-sm font-semibold" style={{ color: "var(--bk-text)" }}>{totalLabel}</span>
          <span className="font-display text-xl font-bold text-right" style={{ color: "var(--bk-text)" }}>{money(totalValue)}</span>
        </div>
      </div>
    </div>
  );
}

export function AgricultureTaxSection({ businessId, business }) {
  const navigate = useNavigate();
  const [method, setMethod] = useState("cash");
  const [year, setYear] = useState(new Date().getFullYear());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [bundleLoading, setBundleLoading] = useState(false);
  const [bundlePreviewLoading, setBundlePreviewLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [sending, setSending] = useState(false);
  const [cpaEmail, setCpaEmail] = useState("");
  const [bundlePreview, setBundlePreview] = useState(null);
  const [bundleModalOpen, setBundleModalOpen] = useState(false);
  const [showGapHints, setShowGapHints] = useState(false);

  const enabled = isAgricultureBusiness(business);
  const years = useMemo(() => {
    const start = new Date().getFullYear();
    return [start, start - 1, start - 2];
  }, []);

  const load = async () => {
    if (!enabled) return;
    setLoading(true);
    try {
      const { data: payload } = await api.get(`/businesses/${businessId}/taxes/schedule-f?year=${year}&method=${method}`);
      setData(payload);
    } catch (err) {
      toast.error(describeApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessId, year, method, enabled]);

  const fetchBundlePreview = async ({ showError = false } = {}) => {
    setBundlePreviewLoading(true);
    try {
      const { data: payload } = await api.get(`/businesses/${businessId}/taxes/year-end-bundle-preview?year=${year}&method=${method}`);
      setBundlePreview(payload);
      return payload;
    } catch (err) {
      if (showError) toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't build the bundle preview.");
      return null;
    } finally {
      setBundlePreviewLoading(false);
    }
  };

  const loadHistory = () => api.get(`/businesses/${businessId}/taxes/year-end-bundle-history`).then((r) => setHistory(r.data || [])).catch(() => {});

  useEffect(() => {
    if (!enabled) return;
    fetchBundlePreview();
    loadHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessId, year, method, enabled]);

  const downloadPdf = async () => {
    setPdfLoading(true);
    try {
      const res = await api.get(`/businesses/${businessId}/taxes/schedule-f.pdf?year=${year}&method=${method}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `schedule-f-${year}-${method}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Downloaded schedule-f-${year}-${method}.pdf`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't generate that PDF.");
    } finally {
      setPdfLoading(false);
    }
  };

  const sendToCpa = async () => {
    if (!cpaEmail.trim()) return toast.error("Enter your CPA email first.");
    setSending(true);
    try {
      await api.post(`/businesses/${businessId}/taxes/schedule-f/email`, { email: cpaEmail.trim(), year, method });
      toast.success(`Sent the farm tax pack to ${cpaEmail.trim()}`);
      setCpaEmail("");
    } catch (err) {
      toast.error(describeApiError(err));
    } finally {
      setSending(false);
    }
  };

  const downloadYearEndBundle = async (targetYear = year, regenerate = false) => {
    setBundleLoading(true);
    try {
      const res = await api.get(`/businesses/${businessId}/taxes/year-end-bundle.zip?year=${targetYear}&method=${method}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `ironroot-year-end-tax-bundle-${targetYear}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      loadHistory();
      if (regenerate) {
        toast.success(`Regenerated your ${targetYear} bundle. Fresh data, same great packaging.`);
      } else {
        toast.success(`Downloaded your ${targetYear} Year-End Tax Bundle`);
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't build the year-end bundle.");
    } finally {
      setBundleLoading(false);
    }
  };

  const openBundlePreview = async () => {
    const payload = await fetchBundlePreview({ showError: true });
    if (!payload) return;
    setShowGapHints(false);
    setBundleModalOpen(true);
  };

  const jumpToGap = (tabTestId) => {
    const tab = document.querySelector(`[data-testid="${tabTestId}"]`);
    if (tab) {
      tab.click();
      tab.scrollIntoView({ behavior: "smooth", block: "center" });
      return true;
    }
    return false;
  };

  const pulseTarget = (selector) => {
    setTimeout(() => {
      const el = document.querySelector(selector);
      if (!el) return;
      const prevOutline = el.style.outline;
      const prevBoxShadow = el.style.boxShadow;
      el.style.outline = "2px solid rgba(240, 180, 41, 0.9)";
      el.style.boxShadow = "0 0 0 0 rgba(240, 180, 41, 0.45)";
      el.animate([
        { boxShadow: "0 0 0 0 rgba(240, 180, 41, 0.45)", transform: "scale(1)" },
        { boxShadow: "0 0 0 14px rgba(240, 180, 41, 0.08)", transform: "scale(1.01)" },
        { boxShadow: "0 0 0 0 rgba(240, 180, 41, 0)", transform: "scale(1)" },
      ], { duration: 1500, iterations: 2 });
      setTimeout(() => {
        el.style.outline = prevOutline;
        el.style.boxShadow = prevBoxShadow;
      }, 3200);
    }, 420);
  };

  const goFixThisNow = (item, closeModal = false) => {
    if (!item) return;
    if (closeModal) setBundleModalOpen(false);
    setShowGapHints(true);
    const path = window.location.pathname;
    const config = {
      receipts_unattached: { url: `${path}?tab=receipts&receipt_gap=unattached`, panel: '[data-testid="receipt-unattached-card"]' },
      mileage: { url: `${path}?tab=mileage`, panel: '[data-testid="mileage-tab"]' },
      taxes_schedule_f: { url: `${path}?tab=taxes`, panel: '[data-testid="ag-tax-schedule-f-income"]' },
      books_uncategorized: { url: `${path}?tab=books&category=Other`, panel: '[data-testid="books-category-filter-pill"]' },
      books: { url: `${path}?tab=books`, panel: '[data-testid="books-ledger"]' },
      taxes: { url: `${path}?tab=taxes`, panel: '[data-testid="taxes-panel"]' },
    };
    const target = config[item.target] || { url: `${path}?tab=taxes`, panel: '[data-testid="taxes-panel"]' };
    navigate(target.url);
    pulseTarget(target.panel);
    toast.info("Moved you to the right area so you can fix that gap.");
  };

  const fixGapsFirst = () => {
    const firstAction = bundlePreview?.warning_items?.[0];
    if (firstAction) {
      goFixThisNow(firstAction, true);
      return;
    }
    setBundleModalOpen(false);
    setShowGapHints(true);
    const meta = bundlePreview?.warning_meta || {};
    if (meta.has_mileage_gap && jumpToGap("tab-mileage")) return;
    if (meta.has_receipt_gap && jumpToGap("tab-receipts")) return;
    const warningCard = document.querySelector('[data-testid="ag-tax-bundle-warnings"]');
    warningCard?.scrollIntoView({ behavior: "smooth", block: "center" });
    toast.info("Highlighted the bundle gaps below.");
  };

  if (!enabled) return null;

  return (
    <div className="bk-card p-6 space-y-6" data-testid="ag-schedule-f-section">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="w-12 h-12 rounded-2xl flex items-center justify-center" style={{ background: "var(--bk-rowan-bg)" }}>
            <Barn size={24} weight="duotone" color="var(--bk-accent)" />
          </div>
          <div>
            <h3 className="font-display text-xl font-bold" style={{ color: "var(--bk-text)" }}>Schedule F + Form 4835</h3>
            <p className="text-sm mt-1 max-w-3xl" style={{ color: "var(--bk-text-2)" }}>
              Agriculture-only tax workflow that auto-sorts farm income, farm expenses, and rental activity into CPA-ready sections.
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select value={year} onChange={(e) => setYear(Number(e.target.value))} data-testid="ag-tax-year" className="rounded-md border px-3 py-2.5 text-sm bg-white outline-none" style={{ borderColor: "var(--bk-border)", color: "var(--bk-text)" }}>
            {years.map((item) => <option key={item} value={item}>{item}</option>)}
          </select>
          <div className="inline-flex rounded-full border p-1 bg-white" style={{ borderColor: "var(--bk-border)" }} data-testid="ag-tax-method-toggle">
            {[
              { key: "cash", label: "Cash" },
              { key: "accrual", label: "Accrual" },
            ].map((item) => (
              <button key={item.key} onClick={() => setMethod(item.key)} data-testid={`ag-tax-method-${item.key}`} className="px-4 py-1.5 rounded-full text-sm font-semibold" style={method === item.key ? { background: "var(--bk-accent)", color: "#fff" } : { color: "var(--bk-text-2)" }}>
                {item.label}
              </button>
            ))}
          </div>
          <button onClick={downloadPdf} disabled={pdfLoading || loading || !data} data-testid="ag-tax-download-pdf" className="bk-btn-primary inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold disabled:opacity-70">
            {pdfLoading ? <CircleNotch size={16} className="animate-spin" /> : <FilePdf size={16} weight="bold" />} Export PDF
          </button>
        </div>
      </div>

      {loading && !data ? (
        <div className="rounded-[24px] border p-5 bg-white" style={{ borderColor: "var(--bk-border)" }} data-testid="ag-tax-loading">
          <p className="text-sm inline-flex items-center gap-2" style={{ color: "var(--bk-text-2)" }}><CircleNotch size={16} className="animate-spin" /> Building your farm tax summary…</p>
        </div>
      ) : data && (
        <>
          <div className="flex flex-wrap items-center justify-between gap-4 rounded-[24px] p-5" style={{ background: "linear-gradient(135deg, rgba(255,249,240,0.98), rgba(246,238,223,0.96))", border: "1px solid rgba(232,168,56,0.24)" }} data-testid="ag-tax-rowan-card">
            <div>
              <p className="text-xs uppercase tracking-[0.16em] font-semibold" style={{ color: "var(--bk-accent)" }}>Rowan says</p>
              <p className="text-sm mt-2 max-w-3xl" style={{ color: "var(--bk-text)" }} data-testid="ag-tax-rowan-primary">{data.rowan_primary}</p>
            </div>
            <div className="flex items-center gap-2 text-sm font-semibold" style={{ color: "var(--bk-success)" }}>
              <ShieldCheck size={18} weight="duotone" /> CPA-ready format
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="rounded-[24px] p-5 text-white" style={{ background: "var(--bk-gradient-primary)" }} data-testid="ag-tax-schedule-f-net-card">
              <p className="text-xs uppercase tracking-wider font-bold" style={{ color: "rgba(255,255,255,0.8)" }}>Schedule F · line 34</p>
              <p className="font-display text-4xl font-bold mt-1">{money(data.schedule_f.net_profit_loss)}</p>
              <p className="text-sm mt-2" style={{ color: "rgba(255,255,255,0.82)" }}>Net farm profit / loss</p>
            </div>
            <div className="rounded-[24px] border p-5 bg-white" style={{ borderColor: "var(--bk-border)" }} data-testid="ag-tax-se-tax-card">
              <div className="flex items-center gap-2">
                <Calculator size={18} weight="duotone" color="var(--bk-accent)" />
                <p className="text-sm font-semibold" style={{ color: "var(--bk-text)" }}>Estimated SE tax</p>
              </div>
              <p className="font-display text-3xl font-bold mt-2" style={{ color: "var(--bk-text)" }}>{money(data.schedule_f.self_employment_tax)}</p>
              <p className="text-xs mt-1" style={{ color: "var(--bk-text-2)" }}>Calculated from Schedule F income subject to Schedule SE</p>
            </div>
            <div className="rounded-[24px] border p-5 bg-white" style={{ borderColor: "var(--bk-border)" }} data-testid="ag-tax-4835-net-card">
              <div className="flex items-center gap-2">
                <Barn size={18} weight="duotone" color="var(--bk-success)" />
                <p className="text-sm font-semibold" style={{ color: "var(--bk-text)" }}>Form 4835 companion</p>
              </div>
              <p className="font-display text-3xl font-bold mt-2" style={{ color: "var(--bk-text)" }}>{money(data.form_4835.net_profit_loss)}</p>
              <p className="text-xs mt-1" style={{ color: "var(--bk-text-2)" }}>Farm rental income / loss</p>
            </div>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            <LineTable title="Schedule F — Part I Farm Income" rows={data.schedule_f.income_lines} totalLabel={data.schedule_f.gross_income_line} totalValue={data.schedule_f.gross_income} testid="ag-tax-schedule-f-income" />
            <LineTable title="Schedule F — Part II Farm Expenses" rows={data.schedule_f.expense_lines} totalLabel={data.schedule_f.total_expense_line} totalValue={data.schedule_f.total_expense} testid="ag-tax-schedule-f-expenses" />
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            <LineTable title="Form 4835 — Farm Rental Income" rows={data.form_4835.income_lines} totalLabel={data.form_4835.gross_income_line} totalValue={data.form_4835.gross_income} testid="ag-tax-4835-income" />
            <LineTable title="Form 4835 — Farm Rental Expenses" rows={data.form_4835.expense_lines} totalLabel={data.form_4835.total_expense_line} totalValue={data.form_4835.total_expense} testid="ag-tax-4835-expenses" />
          </div>

          <div className="rounded-[24px] border p-5 bg-white" style={{ borderColor: "var(--bk-border)" }} data-testid="ag-tax-send-cpa-card">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <h4 className="font-display text-lg font-bold" style={{ color: "var(--bk-text)" }}>Send to CPA</h4>
                <p className="text-sm mt-1 max-w-2xl" style={{ color: "var(--bk-text-2)" }}>
                  Sends the Schedule F + Form 4835 PDF summary through the existing Resend workflow. Your CPA email is entered at send time and isn&apos;t pre-saved.
                </p>
              </div>
              <div className="text-xs" style={{ color: "var(--bk-text-2)" }} data-testid="ag-tax-source-summary">
                {data.method_label} method · {data.source_summary.transaction_count} transaction{data.source_summary.transaction_count === 1 ? "" : "s"}
                {data.method === "accrual" ? ` · ${data.source_summary.accrual_ar_invoices} A/R invoice(s) · ${data.source_summary.accrual_ap_bills} A/P bill(s)` : ""}
              </div>
            </div>
            <div className="flex flex-col md:flex-row gap-3 mt-4">
              <input type="email" value={cpaEmail} onChange={(e) => setCpaEmail(e.target.value)} placeholder="cpa@firm.com" data-testid="ag-tax-cpa-email" className="flex-1 rounded-md border px-3 py-2.5 text-sm outline-none" style={{ borderColor: "var(--bk-border)" }} />
              <button onClick={sendToCpa} disabled={sending} data-testid="ag-tax-send-cpa" className="bk-btn-primary inline-flex items-center justify-center gap-2 px-5 py-2.5 text-sm font-semibold disabled:opacity-70">
                {sending ? <><CircleNotch size={16} className="animate-spin" /> Sending…</> : <><PaperPlaneTilt size={16} weight="fill" /> Send to CPA</>}
              </button>
            </div>
          </div>

          <div className="rounded-[24px] border p-5 bg-white" style={{ borderColor: "var(--bk-border)" }} data-testid="ag-tax-rowan-lines-card">
            <h4 className="font-display text-lg font-bold" style={{ color: "var(--bk-text)" }}>Rowan agriculture tax notes</h4>
            <ul className="space-y-2 text-sm mt-3" style={{ color: "var(--bk-text)" }} data-testid="ag-tax-rowan-lines">
              {(data.rowan_voice_lines || []).map((line) => <li key={line}>• {line}</li>)}
            </ul>
          </div>

          <div className="rounded-[24px] border p-5 bg-white" style={{ borderColor: "var(--bk-border)" }} data-testid="ag-tax-bundle-card">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <h4 className="font-display text-lg font-bold" style={{ color: "var(--bk-text)" }}>Year-End Tax Bundle</h4>
                <p className="text-sm mt-1 max-w-3xl" style={{ color: "var(--bk-text-2)" }}>
                  Downloads one ZIP with Schedule F, Form 4835 when applicable, farm receipts by category, mileage summary, annual P&amp;L, transaction export, and a CPA cover sheet.
                </p>
              </div>
              <button onClick={openBundlePreview} disabled={bundlePreviewLoading || loading || !data} data-testid="ag-tax-download-bundle" className="bk-btn-primary inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold disabled:opacity-70">
                {bundlePreviewLoading ? <CircleNotch size={16} className="animate-spin" /> : <FilePdf size={16} weight="bold" />} Download Year-End Bundle
              </button>
            </div>
            <p className="text-xs mt-3" style={{ color: "var(--bk-text-2)" }} data-testid="ag-tax-bundle-note">
              Rowan packed it exactly the way a CPA wants it: everything your CPA needs, nothing they don&apos;t.
            </p>
            {showGapHints && (bundlePreview?.warnings || []).length > 0 && (
              <div className="mt-4 rounded-2xl border p-4" style={{ borderColor: "#F0C36D", background: "#FFF9ED" }} data-testid="ag-tax-bundle-warnings">
                <div className="flex items-center gap-2 mb-2">
                  <WarningCircle size={18} weight="fill" color="#B45309" />
                  <p className="text-sm font-semibold" style={{ color: "#8A5A13" }}>Fix these bundle gaps first</p>
                </div>
                <ul className="space-y-2 text-sm" style={{ color: "#7C5A28" }}>
                  {(bundlePreview?.warning_items || []).map((item) => (
                    <li key={item.key} className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                      <span>{item.text}</span>
                      <button onClick={() => goFixThisNow(item)} data-testid={`warning-action-${item.key}`} className="text-xs font-semibold underline underline-offset-4 text-left sm:text-right">
                        Go fix this now →
                      </button>
                    </li>
                  ))}
                  {!(bundlePreview?.warning_items || []).length && (bundlePreview?.warnings || []).map((warning) => <li key={warning}>{warning}</li>)}
                </ul>
              </div>
            )}
          </div>
        </>
      )}

      <Dialog open={bundleModalOpen} onOpenChange={setBundleModalOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="ag-tax-bundle-preview-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Preview Bundle Contents</DialogTitle>
            <DialogDescription>
              Review your year-end agriculture bundle, spot missing support, and jump directly to the section that needs cleanup before downloading.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-5">
            <div className="rounded-2xl p-4" style={{ background: (bundlePreview?.warnings || []).length ? "#FFF9ED" : "var(--bk-rowan-bg)", border: "1px solid rgba(232,168,56,0.24)" }} data-testid="ag-tax-bundle-rowan-summary">
              <p className="text-xs uppercase tracking-[0.16em] font-semibold" style={{ color: "var(--bk-accent)" }}>Rowan says</p>
              <p className="text-sm mt-2" style={{ color: "var(--bk-text)" }}>{bundlePreview?.rowan_summary}</p>
            </div>

            <div className="rounded-2xl border overflow-hidden" style={{ borderColor: "var(--bk-border)" }}>
              <div className="px-4 py-3 text-xs font-semibold uppercase tracking-[0.12em]" style={{ background: "var(--bk-page)", color: "var(--bk-text-2)" }}>
                Included in your bundle
              </div>
              <div className="divide-y" style={{ borderColor: "var(--bk-border)" }}>
                {(bundlePreview?.documents || []).map((doc) => (
                  <div key={doc.key} className="px-4 py-3 flex items-start justify-between gap-3" data-testid={`bundle-preview-doc-${doc.key}`}>
                    <div>
                      <p className="text-sm font-semibold" style={{ color: "var(--bk-text)" }}>{doc.label}</p>
                      <p className="text-xs mt-1" style={{ color: "var(--bk-text-2)" }}>{doc.detail}</p>
                    </div>
                    <div className="shrink-0 flex items-center gap-2 text-sm font-semibold" style={{ color: doc.included ? "var(--bk-success)" : "#B23B1F" }}>
                      {doc.included ? <CheckCircle size={18} weight="fill" /> : <XCircle size={18} weight="fill" />}
                      <span>{doc.included ? "✅" : "❌"}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {(bundlePreview?.warnings || []).length > 0 && (
              <div className="rounded-2xl border p-4" style={{ borderColor: "#F0C36D", background: "#FFF9ED" }} data-testid="bundle-preview-gaps">
                <div className="flex items-center gap-2 mb-2">
                  <WarningCircle size={18} weight="fill" color="#B45309" />
                  <p className="text-sm font-semibold" style={{ color: "#8A5A13" }}>Gaps & warnings</p>
                </div>
                <ul className="space-y-2 text-sm" style={{ color: "#7C5A28" }}>
                  {(bundlePreview.warning_items || []).map((item) => (
                    <li key={item.key} className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                      <span>{item.text}</span>
                      <button onClick={() => goFixThisNow(item, true)} data-testid={`modal-warning-action-${item.key}`} className="text-xs font-semibold underline underline-offset-4 text-left sm:text-right">
                        Go fix this now →
                      </button>
                    </li>
                  ))}
                  {!(bundlePreview.warning_items || []).length && bundlePreview.warnings.map((warning) => <li key={warning}>{warning}</li>)}
                </ul>
              </div>
            )}

            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
              <button onClick={fixGapsFirst} data-testid="bundle-preview-fix-gaps" className="px-4 py-2.5 rounded-full text-sm font-semibold border" style={{ borderColor: "var(--bk-border)", color: "var(--bk-text)" }}>
                Fix Gaps First
              </button>
              <button onClick={async () => { setBundleModalOpen(false); await downloadYearEndBundle(); }} disabled={bundleLoading} data-testid="bundle-preview-download-anyway" className="bk-btn-primary inline-flex items-center justify-center gap-2 px-5 py-2.5 text-sm font-semibold disabled:opacity-70">
                {bundleLoading ? <CircleNotch size={16} className="animate-spin" /> : <FilePdf size={16} weight="bold" />} Download Anyway
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <div className="mt-5 rounded-2xl border overflow-hidden" style={{ borderColor: "var(--bk-border)" }} data-testid="bundle-history-card">
        <div className="px-4 py-3 text-xs font-semibold uppercase tracking-[0.12em]" style={{ background: "var(--bk-page)", color: "var(--bk-text-2)" }}>Bundle History</div>
        <div className="divide-y" style={{ borderColor: "var(--bk-border)" }}>
          {history.length === 0 ? (
            <div className="px-4 py-5 text-sm" style={{ color: "var(--bk-text-2)" }} data-testid="bundle-history-empty">No bundle history yet. Your first download will appear here.</div>
          ) : history.map((entry) => (
            <div key={entry.id} className="px-4 py-4 grid grid-cols-1 md:grid-cols-[100px_170px_1fr_auto] gap-3 items-start" data-testid={`bundle-history-row-${entry.id}`}>
              <div>
                <p className="text-xs uppercase tracking-[0.12em]" style={{ color: "var(--bk-text-2)" }}>Tax Year</p>
                <p className="text-sm font-semibold" style={{ color: "var(--bk-text)" }}>{entry.tax_year}</p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-[0.12em]" style={{ color: "var(--bk-text-2)" }}>Generated</p>
                <p className="text-sm font-semibold" style={{ color: "var(--bk-text)" }}>{new Date(entry.generated_at).toLocaleString()}</p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-[0.12em]" style={{ color: "var(--bk-text-2)" }}>Bundle Contents</p>
                <p className="text-sm" style={{ color: "var(--bk-text)" }}>{entry.bundle_summary}</p>
              </div>
              <div className="flex flex-wrap gap-2 md:justify-end">
                <button onClick={() => downloadYearEndBundle(entry.tax_year, false)} data-testid={`bundle-history-download-${entry.id}`} className="px-3 py-2 rounded-full text-xs font-semibold border" style={{ borderColor: "var(--bk-border)", color: "var(--bk-text)" }}>Download Again</button>
                <button onClick={() => downloadYearEndBundle(entry.tax_year, true)} data-testid={`bundle-history-regenerate-${entry.id}`} className="bk-btn-primary px-3 py-2 text-xs font-semibold">Regenerate</button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}